/**
 * Browser uploads, kept outside React so they keep running while the user
 * moves between pages. A file goes to storage in parts: the backend signs a
 * PUT URL per part, the browser sends up to four parts at once, retries a
 * failed part with backoff, and asks the backend to complete the upload.
 */
import { useSyncExternalStore } from 'react'
import { api, json } from '../api/client'
import type { CompletedUpload, PartUrls, UploadView } from '../api/types'

export type UploadPhase = 'starting' | 'uploading' | 'finishing' | 'done' | 'failed' | 'cancelled'

export interface UploadEntry {
  episodeId: string
  workspaceId: string
  filename: string
  size: number
  sent: number
  phase: UploadPhase
  error?: string
  startedAt: number
}

const CONCURRENCY = 4
const URL_BATCH = 50
const MAX_TRIES = 5

class HttpError extends Error {
  constructor(public status: number) {
    super(`Storage answered ${status}.`)
  }
}

class Cancelled extends Error {
  constructor() {
    super('Upload cancelled.')
  }
}

interface Control {
  /** Stop every part: set by the user, or by the first part that gives up. */
  stopped: boolean
  byUser: boolean
  requests: Set<XMLHttpRequest>
  uploadId?: string
}

let entries: Record<string, UploadEntry> = {}
const listeners = new Set<() => void>()
const controls = new Map<string, Control>()

function emit() {
  for (const listener of listeners) listener()
}

function update(episodeId: string, change: Partial<UploadEntry>) {
  const current = entries[episodeId]
  if (!current) return
  entries = { ...entries, [episodeId]: { ...current, ...change } }
  emit()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function isActive(entry: UploadEntry | undefined): boolean {
  return !!entry && (entry.phase === 'starting' || entry.phase === 'uploading' || entry.phase === 'finishing')
}

/** The upload this tab started for an episode, if any. */
export function useUpload(episodeId: string): UploadEntry | undefined {
  return useSyncExternalStore(subscribe, () => entries[episodeId])
}

export function dismissUpload(episodeId: string) {
  if (isActive(entries[episodeId])) return
  const next = { ...entries }
  delete next[episodeId]
  entries = next
  emit()
}

export function cancelUpload(episodeId: string) {
  const control = controls.get(episodeId)
  if (!control) return
  control.byUser = true
  control.stopped = true
  for (const request of control.requests) request.abort()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event) => {
    if (Object.values(entries).some(isActive)) event.preventDefault()
  })
}

function put(url: string, body: Blob, control: Control, onProgress: (loaded: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    control.requests.add(request)
    const done = () => control.requests.delete(request)
    request.open('PUT', url)
    request.upload.onprogress = (event) => onProgress(event.loaded)
    request.onload = () => {
      done()
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new HttpError(request.status))
    }
    request.onerror = () => {
      done()
      reject(new Error('The network dropped while uploading.'))
    }
    request.onabort = () => {
      done()
      reject(new Cancelled())
    }
    request.send(body)
  })
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** Upload a recording as an episode's source. Progress and errors land in the store. */
export async function startUpload(workspaceId: string, episodeId: string, file: File, onFinished?: () => void): Promise<void> {
  if (isActive(entries[episodeId])) return
  const control: Control = { stopped: false, byUser: false, requests: new Set() }
  controls.set(episodeId, control)
  entries = {
    ...entries,
    [episodeId]: { episodeId, workspaceId, filename: file.name, size: file.size, sent: 0, phase: 'starting', startedAt: Date.now() },
  }
  emit()
  try {
    const upload = await api<UploadView>(`/workspaces/${workspaceId}/episodes/${episodeId}/uploads`, {
      method: 'POST',
      body: json({ filename: file.name, content_type: file.type || undefined, size_bytes: file.size }),
    })
    control.uploadId = upload.id
    if (control.stopped) throw new Cancelled()
    update(episodeId, { phase: 'uploading' })

    const urls = new Map<number, string>()
    const loaded = new Map<number, number>()
    let lastReport = 0
    const report = (force = false) => {
      const now = performance.now()
      if (!force && now - lastReport < 250) return
      lastReport = now
      let sent = 0
      for (const bytes of loaded.values()) sent += bytes
      update(episodeId, { sent: Math.min(sent, file.size) })
    }
    const signFrom = async (first: number) => {
      const numbers: number[] = []
      for (let n = first; n <= Math.min(first + URL_BATCH - 1, upload.part_count); n++) numbers.push(n)
      const signed = await api<PartUrls>(`/workspaces/${workspaceId}/uploads/${upload.id}/parts`, {
        method: 'POST',
        body: json({ part_numbers: numbers }),
      })
      for (const part of signed.parts) urls.set(part.part_number, part.url)
    }
    const sendPart = async (n: number) => {
      const start = (n - 1) * upload.part_size
      const blob = file.slice(start, Math.min(start + upload.part_size, file.size))
      for (let attempt = 1; ; attempt++) {
        if (control.stopped) throw new Cancelled()
        if (!urls.has(n)) await signFrom(n)
        const url = urls.get(n)
        if (!url) throw new Error(`No upload URL for part ${n}.`)
        try {
          await put(url, blob, control, (bytes) => {
            loaded.set(n, bytes)
            report()
          })
          loaded.set(n, blob.size)
          report()
          return
        } catch (error) {
          loaded.set(n, 0)
          if (error instanceof Cancelled || control.stopped) throw new Cancelled()
          // An expired signed URL answers 403; sign it again.
          if (error instanceof HttpError && error.status === 403) urls.delete(n)
          if (attempt >= MAX_TRIES) throw error
          await sleep(1000 * 2 ** (attempt - 1))
        }
      }
    }
    let next = 1
    const lane = async () => {
      while (next <= upload.part_count) {
        const n = next++
        await sendPart(n)
      }
    }
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, upload.part_count) }, lane))
    } catch (error) {
      control.stopped = true
      for (const request of control.requests) request.abort()
      throw error
    }
    report(true)
    update(episodeId, { phase: 'finishing' })
    await api<CompletedUpload>(`/workspaces/${workspaceId}/uploads/${upload.id}/complete`, { method: 'POST' })
    update(episodeId, { phase: 'done', sent: file.size })
  } catch (error) {
    if (control.byUser) update(episodeId, { phase: 'cancelled' })
    else update(episodeId, { phase: 'failed', error: error instanceof Error ? error.message : 'The upload failed.' })
    if (control.uploadId) {
      void api<void>(`/workspaces/${workspaceId}/uploads/${control.uploadId}`, { method: 'DELETE' }).catch(() => undefined)
    }
  } finally {
    controls.delete(episodeId)
    onFinished?.()
  }
}
